































































































































import moment from 'moment';
import DatePicker from 'ss-commons-front/src/components/DatePicker.vue';
import { ValidationProvider } from 'vee-validate';
import { Component, Vue, Prop, PropSync } from 'vue-property-decorator';

import { EventDateHelper } from '../services/EventDateHelper';
import toast from './toast';
import { ToastType } from './toast/tost-type.enum';

export type FullHour = { hour: string; minutes: string };

@Component({
  components: {
    DatePicker,
  },
})
export default class DateRangePickerComponent extends Vue {
  @Prop()
  private disableDateInput?: boolean;

  @Prop()
  private showEndDateInput?: boolean;

  @Prop()
  private startDate!: string;

  @PropSync('start-date')
  private startDateSync: string;

  @Prop()
  private endDate!: string;

  @PropSync('end-date')
  private endDateSync: string;

  $refs!: {
    startHourProvider: InstanceType<typeof ValidationProvider>;
    endHourProvider: InstanceType<typeof ValidationProvider>;
    startHourInput: HTMLInputElement;
    endHourInput: HTMLInputElement;
    validationTimeline: InstanceType<typeof ValidationProvider>;
  };

  mounted() {
    this.updateRefValues();
  }

  updateRefValues() {
    this.emptyRefValues();
    this.$refs.startHourInput.value = this.start_hour;
    this.$refs.startHourProvider?.syncValue(this.start_hour);
    this.$refs.endHourInput.value = this.end_hour;
    this.$refs.endHourProvider?.syncValue(this.end_hour);
    if (this.startDate && !this.disableDateInput) {
      this.$refs.validationTimeline?.syncValue(true);
    }
  }

  emptyRefValues() {
    this.$refs.startHourInput.value = '';
    this.$refs.endHourInput.value = '';
  }

  updated() {
    if (this.$refs.startHourInput.value) {
      this.updateRefValues();
    } else {
      this.emptyRefValues();
    }
  }

  getHour = (value: string) => value?.replace(/^.*?T(\d{2}):?(\d{2})?.*?$/, '$1') || '-';

  getMinute = (value: string) => value?.replace(/^.*?T(\d{2}):?(\d{2})?.*?$/, '$2') || '-';

  beforeToday(date: Date) {
    return moment().diff(moment(date), 'days') > 0;
  }

  beforeStart(date: Date) {
    return moment(this.startDate).endOf('day').diff(moment(date).startOf('day'), 'days') > 0;
  }

  get start_date_value(): string {
    return (this.startDate && `Dia: ${moment(this.startDate).format('DD/MM/YYYY')}`) || 'Selecione a data inicial';
  }

  get end_date_value(): string {
    return (this.endDate && `Dia: ${moment(this.endDate).format('DD/MM/YYYY')}`) || 'Selecione a data final';
  }

  get start_hour(): string {
    return moment(this.startDate).format('HH:mm');
  }

  get end_hour(): string {
    return moment(this.endDate).format('HH:mm');
  }

  changeStartDate(value: string) {
    if (!value) {
      return;
    }
    if (!this.endDate || new Date(this.endDate) <= new Date(value)) {
      this.endDateSync = new Date(value).toISOString();
    }
    this.startDateSync = new Date(value).toISOString();
  }

  changeEndDate(value: string) {
    if (!value) {
      return;
    }
    this.endDateSync = value && new Date(value).toISOString();
  }

  getHourAndMinute(value: string): FullHour {
    return {
      hour: this.getHour(value),
      minutes: this.getMinute(value),
    };
  }

  changeHour(date: Date, hour: string, minutes: string): Date {
    const newHour = Number(hour);
    const newMinutes = Number(minutes);

    return moment(date.toISOString()).hours(newHour).minutes(newMinutes).toDate();
  }

  onHourChange(value: string, $ref: 'Start' | 'End', date: Date): string {
    if (value?.length < 2) {
      return null;
    }
    const { hour, minutes } = this.getHourAndMinute(`T${value}`);
    return this.changeHour(date, hour, minutes).toISOString();
  }

  changeStartHour($event: any) {
    const value: string = $event.target.value as string;
    return this.changeStartDate(this.onHourChange(value, 'Start', new Date(this.startDate || null)));
  }

  validateEndHour(value: string, syncValue: (value: string) => void) {
    if (value.length !== 5) {
      return;
    }
    const newEndDate = moment(this.endDate)
      .hours(Number(this.getHour(`T${value}`)))
      .minutes(Number(this.getMinute(`T${value}`)))
      .toISOString();
    const sameDay: boolean = EventDateHelper.isSameDay({ start: this.startDate, end: newEndDate });
    const differenceInMinutes = moment(newEndDate)
      .add(Number(value), 'minutes')
      .diff(moment(this.startDate), 'minutes');
    if (sameDay && differenceInMinutes < 0) {
      toast('Atenção ! O horário final é inferior ao horário inicial no mesmo dia.', ToastType.WARNING, {
        id: 'hour-warning',
        timeout: 8000,
        displayMode: 2,
      });
      return syncValue('99:99');
    }
    return syncValue(value);
  }

  changeEndHour($event: any, syncValue: (value: string) => void) {
    const value: string = $event.target?.value as string;
    if (value?.length !== 5) {
      return;
    }
    this.validateEndHour(value, syncValue);
    const newDateHour = this.onHourChange(value, 'End', new Date(this.endDate || this.startDate || null));
    return this.changeEndDate(newDateHour);
  }

  public isStartHourNotFilled() {
    const { value } = this.$refs.startHourInput;
    return value === '' || value === '-:-';
  }

  public isEndHourNotFilled() {
    const { value } = this.$refs.endHourInput;
    return value === '' || value === '-:-';
  }
}
